import { useCallback, useState } from "react";
import { countryCodeToCountryName } from "@/utils/countries.util";
import type { LocationAutocompleteFilterDto } from "@advanced-sales-technologies/backend-client/models/LocationAutocompleteFilterDto";
import { useTranslation } from "react-i18next";

import { useLocationAutocomplete } from "@/hooks/api/search/useLocationAutocomplete";
import useDebounceQuery from "@/hooks/useDebounceQuery";
import Chip from "@/components/atoms/Chip";
import LocationSelectItem from "@/components/molecules/filter/LocationSelectItem";
import FormComboBox from "@/components/molecules/form/FormComboBox";
import { FormControlProps } from "@/components/molecules/form/FormControl";

type FormLocationAutocompleteProps = {
  name: string;
  onQueryChange?: (query: string) => void;
  disabled?: boolean;
  config?: FormLocationAutocompleteConfig;
  multiple?: boolean;
  showOptionsOnFocus?: boolean;
} & Omit<FormControlProps, "id" | "children">;

type FormLocationAutocompleteConfig = {
  filter?: LocationAutocompleteFilterDto;
  resultItems?: Array<"COUNTRY_CODE" | "CITY" | "ZIPCODE" | "STATE" | "REGION">;
};

const FormLocationAutocomplete = ({
  name,
  onQueryChange,
  disabled,
  config,
  multiple,
  showOptionsOnFocus = true,
  ...rest
}: FormLocationAutocompleteProps) => {
  const { t } = useTranslation();
  const [locationIncludeQuery, setLocationIncludeQuery] = useState("");

  const {
    query: locationInclude,
    setDebounceQuery: setLocationInclude,
    isTyping: locationIncludeTyping,
  } = useDebounceQuery();

  const { data: includedLocationData, isLoading: isLoadingLocationInclude } = useLocationAutocomplete({
    query: locationInclude,
    ...config,
  });

  const country = useCallback((countryCode: string) => countryCodeToCountryName(countryCode), []);

  return (
    <FormComboBox
      name={name}
      query={locationIncludeQuery}
      setQuery={(query) => {
        setLocationIncludeQuery(query);
        setLocationInclude(query);
        onQueryChange?.(query);
      }}
      disabled={disabled}
      optionsSearchable={false}
      showOptionsOnFocus={showOptionsOnFocus}
      multiple={true}
      placeholder={t("search.filter.location.placeholder")}
      options={includedLocationData || []}
      isLoading={isLoadingLocationInclude || locationIncludeTyping}
      renderOption={(option) => <LocationSelectItem location={option} />}
      renderChip={(option, removeOption) => (
        <Chip
          data-testid={`selected-option-${option.id}`}
          size={"xs"}
          color={"natural"}
          key={option.id}
          label={option.name === option.country_code ? country(option.name) : option.name}
          className={"max-w-28"}
          onRemove={() => removeOption(option)}
        />
      )}
      {...rest}
    />
  );
};

export default FormLocationAutocomplete;
